
import templatesQuery from "@/apollo/queries/pages/templates.gql";
import lenisScrollMixin from "@/mixins/lenisScrollMixin";
import previewModeMixin from "@/mixins/previewModeMixin";
import seoMixin from "@/mixins/seoMixin";

export default {
  name: "templates",
  colorMode: "dark",
  mixins: [lenisScrollMixin, previewModeMixin, seoMixin],
  data() {
    return {
      templates: null,          // CMS content for the page
      templatesPosts: [],       // Array of widget templates from the API
      previewTemplate: false,   // Controls whether the preview modal is open
      targetTemplatePostIndex: null, // Index of the currently selected template
      previewMode: false,
      availableFonts: [
        "Anton", "Be Vietnam Pro", "Bitter", "Chakra Petch", "Hahmlet",
        "Inconsolata", "Inter", "Kosugi", "Lato", "Lora", "M PLUS 1 Code",
        "Montserrat", "Noto Sans", "Noto Serif", "Nunito", "Open Sans",
        "PT Sans", "PT Serif", "Pridi", "Raleway", "Roboto",
        "Roboto Slab", "Shadows Into Light", "Source Sans Pro", "Titillium Web",
        "Ubuntu Mono"
      ]
    };
  },
  async asyncData({app, store}) {
    const previewMode = store.state.previewMode;
    if (previewMode) {
      await app.apolloProvider.defaultClient.resetStore();
    }
    // Fetch CMS content via Apollo (for header, details, SEO, etc.)
    const responseTemplates = await app.apolloProvider.defaultClient
      .query({
        query: templatesQuery,
        variables: {
          locale: app.i18n?.locale || "en",
        },
      })
      .catch((err) => {
        console.error("GraphQL error:", JSON.stringify(err, null, 2));
      });
    const templates = responseTemplates.data?.templates;

    try {
      // Fetch widget templates from API
      const widgetTemplates = await app.$axios.$get("/v1/api/widget-templates/?template_theme=default");
      // Return the CMS content along with the raw API response.
      return {
        templates,
        previewMode,
        templatesPosts: widgetTemplates,
      };
    } catch (err) {
      console.error("REST API request error:", err);
      return {
        templates,
        previewMode,
        templatesPosts: [],
      };
    }
  },
  methods: {
    loadGoogleFonts() {
      const fontFamilies = this.availableFonts
        .map(font => font.replace(/ /g, "+") + ":wght@400;700")
        .join("&family=");

      const fontURL = `https://fonts.googleapis.com/css2?family=${fontFamilies}&display=swap`;

      // Check if the font link already exists to prevent duplicate loads
      if (!document.querySelector(`link[href="${fontURL}"]`)) {
        const link = document.createElement("link");
        link.href = fontURL;
        link.rel = "stylesheet";
        link.as = "style";
        link.onload = () => (link.rel = "stylesheet");
        document.head.appendChild(link);
      }
    },
    triggerPreviewWidget(id) {
      // Find the index of the template within templatesPosts by matching the ID
      const index = this.templatesPosts.findIndex((item) => item.id === id);

      // If the template does not exist, gracefully exit
      if (index === -1) {
        console.error(`Template with id ${id} not found in templatesPosts`);
        return;
      }

      // Update state with the found index and open the modal
      this.setTargetTemplateIndex(index);
      this.setPreviewTemplate(true);

      const template = this.templatesPosts[index];
      // Use the slug from the API or generate one if missing.
      const slug =
        template.slug ||
        template.name.toLowerCase().replace(/\s+/g, "-");

      // Update the URL using history.pushState.
      history.pushState(
        {modalOpen: true, templateIndex: index},
        "",
        `${this.$i18n.locale === this.$i18n.defaultLocale ? "" : "/" + this.$i18n.locale}/templates/${slug}/`
      );
    },
    setTargetTemplateIndex(index) {
      this.targetTemplatePostIndex = index;
    },
    setPreviewTemplate(value) {
      this.previewTemplate = value;
      this.$store.dispatch("triggerScrollDisabled", value);
    },
    closeModal() {
      this.setPreviewTemplate(false);
      history.back();
    },
    // Handles the popstate event for back/forward navigation
    handlePopState(event) {
      if (event.state && event.state.modalOpen) {
        // Open the modal with the correct template
        const index = event.state.templateIndex;
        this.setTargetTemplateIndex(index);
        this.setPreviewTemplate(true);
      } else {
        // Close the modal
        this.setPreviewTemplate(false);
      }
    }
  },
  mounted() {
    window.addEventListener("popstate", this.handlePopState);

    // Load Google Fonts dynamically
    this.loadGoogleFonts();

    const pathParts = this.$route.path.split("/");
    if (pathParts.length === 3 && pathParts[1] === "templates") {
      const slug = pathParts[2];
      const index = this.templatesPosts.findIndex(item => item.slug === slug);
      if (index !== -1) {
        history.replaceState({modalOpen: true, templateIndex: index}, '', this.$route.fullPath);
        this.setTargetTemplateIndex(index);
        this.setPreviewTemplate(true);
      }
    } else {
      history.replaceState({modalOpen: false}, "", this.$route.fullPath);
    }
  },
  beforeDestroy() {
    window.removeEventListener("popstate", this.handlePopState);
  }
}
